export type Point = { x: number; y: number };

interface Base {
  id: string;
  page: number;
  color: string;
}

export interface PenAnnotation extends Base {
  type: "pen";
  points: Point[];
  width: number;
}

export interface TextAnnotation extends Base {
  type: "text";
  x: number;
  y: number;
  text: string;
  size: number;
}

export interface BoxAnnotation extends Base {
  type: "rect" | "ellipse";
  x: number;
  y: number;
  w: number;
  h: number;
  filled: boolean;
  width: number;
}

export interface ArrowAnnotation extends Base {
  type: "arrow";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
}

export interface SignatureAnnotation extends Base {
  type: "signature";
  x: number;
  y: number;
  w: number;
  h: number;
  image: string;
}

export type Annotation =
  | PenAnnotation
  | TextAnnotation
  | BoxAnnotation
  | ArrowAnnotation
  | SignatureAnnotation;

export type Bounds = { x: number; y: number; w: number; h: number };

export const TEXT_LINE_HEIGHT = 1.25;
export const TEXT_FONT =
  '"PingFang TC","Noto Sans TC","Microsoft JhengHei",sans-serif';

let idSeq = 0;
export const newAnnotationId = () =>
  `a${Date.now().toString(36)}${(idSeq++).toString(36)}`;

export function normalizeBox<T extends Bounds>(box: T): T {
  return {
    ...box,
    x: box.w < 0 ? box.x + box.w : box.x,
    y: box.h < 0 ? box.y + box.h : box.y,
    w: Math.abs(box.w),
    h: Math.abs(box.h),
  };
}

let measureCtx: CanvasRenderingContext2D | null = null;
function measureText(text: string, size: number) {
  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
  if (!measureCtx) return text.length * size;
  measureCtx.font = `${size}px ${TEXT_FONT}`;
  return measureCtx.measureText(text).width;
}

export function annotationBounds(a: Annotation): Bounds {
  switch (a.type) {
    case "pen": {
      const xs = a.points.map((p) => p.x);
      const ys = a.points.map((p) => p.y);
      const pad = a.width / 2;
      const minX = Math.min(...xs) - pad;
      const minY = Math.min(...ys) - pad;
      return {
        x: minX,
        y: minY,
        w: Math.max(...xs) + pad - minX,
        h: Math.max(...ys) + pad - minY,
      };
    }
    case "text": {
      const lines = a.text.split("\n");
      return {
        x: a.x,
        y: a.y,
        w: Math.max(...lines.map((l) => measureText(l, a.size)), a.size),
        h: lines.length * a.size * TEXT_LINE_HEIGHT,
      };
    }
    case "arrow": {
      const pad = a.width * 3;
      return {
        x: Math.min(a.x1, a.x2) - pad,
        y: Math.min(a.y1, a.y2) - pad,
        w: Math.abs(a.x2 - a.x1) + pad * 2,
        h: Math.abs(a.y2 - a.y1) + pad * 2,
      };
    }
    default:
      return { x: a.x, y: a.y, w: a.w, h: a.h };
  }
}

export function moveAnnotation(a: Annotation, dx: number, dy: number): Annotation {
  switch (a.type) {
    case "pen":
      return { ...a, points: a.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
    case "arrow":
      return { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy };
    default:
      return { ...a, x: a.x + dx, y: a.y + dy };
  }
}

export const isResizable = (
  a: Annotation,
): a is BoxAnnotation | SignatureAnnotation =>
  a.type === "rect" || a.type === "ellipse" || a.type === "signature";

export function resizeAnnotation(
  a: BoxAnnotation | SignatureAnnotation,
  w: number,
  h: number,
): Annotation {
  const min = 8;
  if (a.type === "signature") {
    const ratio = a.h / a.w;
    const nw = Math.max(min, w);
    return { ...a, w: nw, h: nw * ratio };
  }
  return { ...a, w: Math.max(min, w), h: Math.max(min, h) };
}

function distanceToSegment(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = dx * dx + dy * dy;
  const t = len ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function hitTest(a: Annotation, p: Point, tolerance: number): boolean {
  switch (a.type) {
    case "pen":
      if (a.points.length === 1) return Math.hypot(p.x - a.points[0].x, p.y - a.points[0].y) <= tolerance + a.width;
      return a.points.some(
        (pt, i) => i > 0 && distanceToSegment(p, a.points[i - 1], pt) <= tolerance + a.width / 2,
      );
    case "arrow":
      return distanceToSegment(p, { x: a.x1, y: a.y1 }, { x: a.x2, y: a.y2 }) <= tolerance + a.width / 2;
    case "rect":
    case "ellipse": {
      const b = a;
      const inside =
        p.x >= b.x - tolerance && p.x <= b.x + b.w + tolerance && p.y >= b.y - tolerance && p.y <= b.y + b.h + tolerance;
      if (!inside) return false;
      if (b.filled) return true;
      if (a.type === "rect") {
        return (
          Math.abs(p.x - b.x) <= tolerance + b.width ||
          Math.abs(p.x - (b.x + b.w)) <= tolerance + b.width ||
          Math.abs(p.y - b.y) <= tolerance + b.width ||
          Math.abs(p.y - (b.y + b.h)) <= tolerance + b.width
        );
      }
      const rx = b.w / 2;
      const ry = b.h / 2;
      const nx = (p.x - (b.x + rx)) / Math.max(rx, 1);
      const ny = (p.y - (b.y + ry)) / Math.max(ry, 1);
      const r = Math.sqrt(nx * nx + ny * ny);
      return Math.abs(r - 1) * Math.min(rx, ry) <= tolerance + b.width;
    }
    default: {
      const b = annotationBounds(a);
      return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
    }
  }
}

const imageCache = new Map<string, HTMLImageElement>();

export function getCachedImage(src: string, onLoad?: () => void) {
  let img = imageCache.get(src);
  if (!img) {
    img = new Image();
    img.onload = () => onLoad?.();
    img.src = src;
    imageCache.set(src, img);
  } else if (!img.complete && onLoad) {
    img.addEventListener("load", onLoad, { once: true });
  }
  return img;
}

export async function preloadImages(annotations: Annotation[]) {
  await Promise.all(
    annotations
      .filter((a): a is SignatureAnnotation => a.type === "signature")
      .map((a) => {
        const img = getCachedImage(a.image);
        if (img.complete && img.naturalWidth) return undefined;
        return img.decode().catch(() => undefined);
      }),
  );
}

export function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  a: ArrowAnnotation,
) {
  const angle = Math.atan2(a.y2 - a.y1, a.x2 - a.x1);
  const len = Math.max(10, a.width * 4);
  ctx.beginPath();
  ctx.moveTo(a.x2, a.y2);
  ctx.lineTo(a.x2 - len * Math.cos(angle - Math.PI / 7), a.y2 - len * Math.sin(angle - Math.PI / 7));
  ctx.lineTo(a.x2 - len * Math.cos(angle + Math.PI / 7), a.y2 - len * Math.sin(angle + Math.PI / 7));
  ctx.closePath();
  ctx.fill();
}

export function drawAnnotation(ctx: CanvasRenderingContext2D, a: Annotation, onImageLoad?: () => void) {
  ctx.save();
  ctx.strokeStyle = a.color;
  ctx.fillStyle = a.color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  switch (a.type) {
    case "pen": {
      ctx.lineWidth = a.width;
      if (a.points.length === 1) {
        ctx.beginPath();
        ctx.arc(a.points[0].x, a.points[0].y, a.width / 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      ctx.beginPath();
      ctx.moveTo(a.points[0].x, a.points[0].y);
      for (let i = 1; i < a.points.length - 1; i++) {
        const mx = (a.points[i].x + a.points[i + 1].x) / 2;
        const my = (a.points[i].y + a.points[i + 1].y) / 2;
        ctx.quadraticCurveTo(a.points[i].x, a.points[i].y, mx, my);
      }
      const last = a.points[a.points.length - 1];
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
      break;
    }
    case "text": {
      ctx.font = `${a.size}px ${TEXT_FONT}`;
      ctx.textBaseline = "top";
      a.text.split("\n").forEach((line, i) => {
        ctx.fillText(line, a.x, a.y + i * a.size * TEXT_LINE_HEIGHT);
      });
      break;
    }
    case "rect": {
      if (a.filled) ctx.fillRect(a.x, a.y, a.w, a.h);
      else {
        ctx.lineWidth = a.width;
        ctx.strokeRect(a.x, a.y, a.w, a.h);
      }
      break;
    }
    case "ellipse": {
      ctx.beginPath();
      ctx.ellipse(a.x + a.w / 2, a.y + a.h / 2, a.w / 2, a.h / 2, 0, 0, Math.PI * 2);
      if (a.filled) ctx.fill();
      else {
        ctx.lineWidth = a.width;
        ctx.stroke();
      }
      break;
    }
    case "arrow": {
      ctx.lineWidth = a.width;
      ctx.beginPath();
      ctx.moveTo(a.x1, a.y1);
      ctx.lineTo(a.x2, a.y2);
      ctx.stroke();
      drawArrowHead(ctx, a);
      break;
    }
    case "signature": {
      const img = getCachedImage(a.image, onImageLoad);
      if (img.complete && img.naturalWidth) ctx.drawImage(img, a.x, a.y, a.w, a.h);
      break;
    }
  }
  ctx.restore();
}

export function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  onImageLoad?: () => void,
) {
  annotations.forEach((a) => drawAnnotation(ctx, a, onImageLoad));
}
